import { take } from 'rxjs/operators';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { JobManagementService, JobManagementModel } from '@app/origami-api/services/job-management.service';
import { ErrorCode } from '@app/origami-api/constants';
import { BackgroundJob } from '../../background-job';
import {
  DialogService,
  ShowErrorsDialogOptions,
  StandardErrorDialogOptions,
} from '@app/services/dialogs/dialog.service';
import { BackgroundJobResultComponent } from '../background-job-result/background-job-result.component';
import { getErrorDescription } from '@app/origami-api/utils/error-utils';
import { OrigamiHttpResponse } from '@app/origami-api/services/base/service.base';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';

@Component({
  standalone: false,
  selector: 'ti-background-job',
  templateUrl: './background-job.component.html',
  styleUrls: ['./background-job.component.sass'],
})
export class BackgroundJobComponent {
  readonly strings = tiStrings.services.backgroundJobs.components.backgroundJob;
  readonly miscStrings = tiStrings.app.misc;
  @Input() set data(job: BackgroundJob) {
    this.setJob(job);
  }
  @Input() closable = true;
  @Input() cancelable = true;
  @Input() hideResult = false;
  @Input() useActionAsTitle = false;
  @Input() showJobResultSuccess: (jobResult: JobManagementModel) => void;
  @Input() showJobResultError: (jobResult: JobManagementModel) => void;
  @Input() jobResultSuccessParser: (jobResult: JobManagementModel) => string;
  @Input() jobResultErrorParser: (jobResult: JobManagementModel) => string;
  @Input() jobStatusMessageParser: (job: BackgroundJob) => string;
  @Input() showResultAfterFinish: boolean;
  @Input() standardErrorDialogOptions: StandardErrorDialogOptions;
  @Input() customCompletionDetailsPattern: string;
  @Output() close = new EventEmitter<string>();

  startTime: string;
  job: BackgroundJob;
  message: string;

  get progressPercentage(): number {
    if (!this.job?.progress || !this.job.progress.total || this.job.progress.total === 0) {
      return 0;
    }
    return Math.min(100, (this.job.progress.current / this.job.progress.total) * 100);
  }

  get showProgress(): boolean {
    const hasErrors = this.job?.completedWithErrors && !this.job?.canceled;
    const isCanceled = this.job?.canceled;
    const isCompleted = this.job?.completed;

    if (hasErrors || isCanceled || isCompleted) {
      return false;
    }

    return this.job?.inProgress === true;
  }

  get isProgressComplete(): boolean {
    return this.job?.progress && this.job.progress.total > 0 && this.job.progress.current >= this.job.progress.total;
  }

  get completionStatus(): string {
    if (this.job?.succeed && this.job?.progress) {
      return this.strings.completed;
    }
    return '';
  }

  get completionDetailsPattern(): string {
    return this.customCompletionDetailsPattern ?? this.strings.documentsProgressPattern;
  }

  get completionDetails(): string {
    if (this.job?.succeed && this.job?.progress && this.job.progress.total > 0) {
      return tiStringsFormat(this.completionDetailsPattern, this.job.progress.current, this.job.progress.total);
    }
    return '';
  }

  get jobProcessName(): string {
    if (this.useActionAsTitle) {
      return this.job?.progress?.action || this.job?.result?.Action || this.job?.title;
    }
    return this.job?.title;
  }

  get showGetJobResultButton(): boolean {
    if (!this.job?.completed || this.job.canceled) {
      return false;
    }

    // Even when result is normally hidden (e.g. publish), show the button for minor/errors
    // so the user can open ErrorRecords (e.g. ErrorCode 510).
    return !this.hideResult || this.job.completedWithErrors;
  }

  constructor(
    private jobManagementService: JobManagementService,
    private dialogService: DialogService,
  ) {}

  closeJob() {
    this.close.emit(this.job.id);
  }

  cancelJob() {
    this.cancelable = false;
    this.jobManagementService.cancelJob(this.job.siteId, this.job.id).pipe(take(1)).subscribe();
  }

  showJobResult() {
    if (this.job.succeed && this.showJobResultSuccess) {
      this.showJobResultSuccess(this.job.result);
    } else if (this.job.completedWithErrors && this.showJobResultError) {
      this.showJobResultError(this.job.result);
    } else {
      this.showJobResultDefault();
    }

    if (this.closable) {
      this.closeJob();
    }
  }

  private setJob(job: BackgroundJob) {
    this.job = job;
    this.startTime = this.timeToString(job.startTime);
    this.message = this.getJobStatusMessage(job, true);

    if (job.completed && this.showResultAfterFinish) {
      this.showJobResult();
    }
  }

  private showJobResultDefault() {
    const jobResult = this.job.result.Result;
    const link = jobResult && (jobResult.startsWith('http://') || jobResult.startsWith('https://')) ? jobResult : null;

    if (this.job.succeed && link) {
      return window.open(link);
    }
    const userStandardErrorDialog =
      this.standardErrorDialogOptions && this.job.errorCode === ErrorCode.FINISHED_WITH_MINOR_ERRORS;
    if (userStandardErrorDialog) {
      const response: OrigamiHttpResponse = {
        ErrorCode: this.job.errorCode,
        Result: this.job.result,
        ErrorMessage: this.getCustomJobResultText(),
      };
      const errorDialogOptions: ShowErrorsDialogOptions = {
        response,
      };
      if (this.standardErrorDialogOptions.columns?.length) {
        errorDialogOptions.columns = this.standardErrorDialogOptions.columns;
      }
      if (this.standardErrorDialogOptions.standardErrorDialogDataGetter) {
        errorDialogOptions.data = this.standardErrorDialogOptions.standardErrorDialogDataGetter(this.job.result);
      }
      this.dialogService.showErrors(errorDialogOptions);
    } else {
      const errorRecords = this.job.result.ErrorRecords || [];
      const chekCollision =
        errorRecords.length && errorRecords.every((item) => item.ErrorCode === ErrorCode.TOPIC_NAMES_COLLISION);
      chekCollision
        ? this.dialogService.showErrors({
            comment: getErrorDescription(ErrorCode.TOPIC_NAMES_COLLISION),
            title: `${tiStrings.services.backgroundJobs.components.stringsBackgroundJobResult.jobResult}`,
            columns: [
              { name: 'errorObjectName', title: tiStrings.app.misc.documentName, width: 100 },
              { name: 'errorDescription', title: tiStrings.app.misc.errorDescription, width: 100 },
            ],
            data: errorRecords.map((error) => ({
              errorObjectName: error.ObjectName,
              errorDescription: error.ErrorMessage || getErrorDescription(error.ErrorCode),
            })),
          })
        : this.dialogService.showDialog(
            BackgroundJobResultComponent,
            (instance: BackgroundJobResultComponent, close: (res: any) => void) => {
              instance.text = this.getCustomJobResultText();
              instance.errors = (this.job.result.ErrorRecords || []).map((item) => ({
                name: item.ObjectName,
                description: item.ErrorMessage || getErrorDescription(item.ErrorCode),
              }));

              instance.close.subscribe((res) => close(res));
            },
          );
    }
  }

  private getCustomJobResultText() {
    const text = this.job.succeed
      ? this.jobResultSuccessParser
        ? this.jobResultSuccessParser(this.job.result)
        : null
      : this.jobResultErrorParser
        ? this.jobResultErrorParser(this.job.result)
        : null;
    return text || this.getJobStatusMessage(this.job);
  }

  private getJobStatusMessage(job: BackgroundJob, useJobStatusMessageParser?: boolean): string {
    if (useJobStatusMessageParser && this.jobStatusMessageParser) {
      const message = this.jobStatusMessageParser(job);
      if (message) {
        return message;
      }
    }
    if (this.job.succeed) {
      return this.strings.operationWasSuccessfullyCompleted;
    }
    if (job.canceled) {
      return this.strings.canceled;
    }
    if (this.job.result && this.job.result.ErrorMessage) {
      return this.job.result.ErrorMessage;
    }
    if (!job.errorCode) {
      return job.status;
    }

    switch (job.errorCode) {
      case ErrorCode.TASK_COMPLETED_WITH_ERRORS:
      case ErrorCode.FINISHED_WITH_MINOR_ERRORS:
      case ErrorCode.ROOM_NAME_ALREADY_EXISTS:
      case ErrorCode.ANOTHER_EXPORT_TASK_PENDING:
        return this.strings.taskIsCompletedWithMinorErrors;
      case ErrorCode.REG_PACKET_SENDING_ERROR:
        return tiStringsFormat(
          getErrorDescription(ErrorCode.REG_PACKET_SENDING_ERROR),
          tiStrings.shared.emailDialog.regulatoryPacket,
        );
      default:
        return getErrorDescription(job.errorCode) || getErrorDescription('unknown');
    }
  }

  private timeToString(date: Date): string {
    const time = date.toLocaleTimeString('en-US', { hour12: false, hour: 'numeric', minute: 'numeric' });
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();

    return `${day} ${month} ${time}`;
  }
}
